import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';

// Font Awesome
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

// Charts
import { NgChartsModule } from 'ng2-charts';

// Third-party
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { ImageCropperModule } from 'ngx-image-cropper';
import { AngularEditorModule } from '@kolkov/angular-editor';
import { NgxMatColorPickerModule } from '@angular-material-components/color-picker';

// CDK
import { ScrollingModule } from '@angular/cdk/scrolling';
import { DragDropModule } from '@angular/cdk/drag-drop';

// Material Form Controls
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule, MatRippleModule } from '@angular/material/core';
import {
  MomentDateModule,
  MatMomentDateModule
} from '@angular/material-moment-adapter';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSliderModule } from '@angular/material/slider';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

// Material Navigation
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';

// Material Layout
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatListModule } from '@angular/material/list';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTreeModule } from '@angular/material/tree';

// Material Buttons & Indicators
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatBadgeModule } from '@angular/material/badge';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';

// Material Popups & Modals
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { MatDialogModule } from '@angular/material/dialog';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';

// Material Data tables
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';

// ng-zorro
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzStepsModule } from 'ng-zorro-antd/steps';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzRateModule } from 'ng-zorro-antd/rate';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzAutocompleteModule } from 'ng-zorro-antd/auto-complete';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzCarouselModule } from 'ng-zorro-antd/carousel';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzProgressModule } from 'ng-zorro-antd/progress';
import { NzTimePickerModule } from 'ng-zorro-antd/time-picker';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzI18nModule } from 'ng-zorro-antd/i18n';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzUploadModule } from 'ng-zorro-antd/upload';
import { NzPopoverModule } from 'ng-zorro-antd/popover';

// Shared widget components
import { LoaderComponent } from '../secure/dashboard/components/loader/loader.component';
import { BaseComponent } from '../secure/dashboard/components/base/base.component';
import { UserSelectComponent } from '../secure/dashboard/components/user-select/user-select.component';
import { SectionDescriptionComponent } from '../secure/dashboard/components/section-description/section-description.component';
import { AlertComponent } from '../secure/dashboard/components/_shared/alert/alert.component';
import { SvgComponentComponent } from '../secure/dashboard/components/_shared/svg-component/svg-component.component';

// Shared _shared/widgets
import { ProfileWidgetComponent } from '../secure/dashboard/components/_shared/widgets/profile-widget/profile-widget.component';
import { GamificationWidgetComponent } from '../secure/dashboard/components/_shared/widgets/gamification-widget/gamification-widget.component';
import { GenericChartWidgetComponent } from '../secure/dashboard/components/_shared/generic-chart-widget/generic-chart-widget.component';
import { MonitoringWidgetComponent } from '../secure/dashboard/components/_shared/widgets/monitoring-widget/monitoring-widget.component';
import { HowIsGoingComponent } from '../secure/dashboard/components/_shared/widgets/how-is-going/how-is-going.component';
import { ReflectionWidgetComponent } from '../secure/dashboard/components/_shared/widgets/reflection-widget/reflection-widget.component';
import { GenericReflectionWidgetComponent } from '../secure/dashboard/components/_shared/generic-reflection-widget/generic-reflection-widget.component';
import { UsersPerformanceComponent } from '../secure/dashboard/components/_shared/widgets/users-performance/users-performance.component';
import { SbiWidgetComponent } from '../secure/dashboard/components/_shared/widgets/sbi-widget/sbi-widget.component';
import { GenericSbiCardComponent } from '../secure/dashboard/components/_shared/generic-sbi-card/generic-sbi-card.component';
import { Fb360WidgetComponent } from '../secure/dashboard/components/_shared/widgets/fb360-widget/fb360-widget.component';
import { ChartTransversalComponent } from '../secure/dashboard/components/_shared/chart-transversal/chart-transversal.component';
import { MonitoringIncrementalDeltaComponent } from '../secure/dashboard/components/_shared/widgets/monitoring-incremental-delta/monitoring-incremental-delta.component';
import { MonitoringIncrementalDeltaCardComponent } from '../secure/dashboard/components/_shared/widgets/monitoring-incremental-delta/monitoring-incremental-delta-card/monitoring-incremental-delta-card.component';

// Components used across multiple feature modules
import { CardMonitoraggioComponent } from '../secure/dashboard/components/card-monitoraggio/card-monitoraggio.component';
import { MonitoringTableComponent } from '../secure/dashboard/monitoraggio/monitoring-table/monitoring-table.component';
import { ModaleProfiloComponent } from '../secure/dashboard/components/modale-profilo/modale-profilo.component';
import { ImageFallbackDirective } from './directives/image-fallback.directive';

const MATERIAL_MODULES = [
  MatAutocompleteModule,
  MatCheckboxModule,
  MatDatepickerModule,
  MatNativeDateModule,
  MomentDateModule,
  MatMomentDateModule,
  MatFormFieldModule,
  MatInputModule,
  MatRadioModule,
  MatSelectModule,
  MatSliderModule,
  MatSlideToggleModule,
  MatMenuModule,
  MatSidenavModule,
  MatToolbarModule,
  MatCardModule,
  MatDividerModule,
  MatExpansionModule,
  MatGridListModule,
  MatListModule,
  MatStepperModule,
  MatTabsModule,
  MatTreeModule,
  MatButtonModule,
  MatButtonToggleModule,
  MatBadgeModule,
  MatChipsModule,
  MatIconModule,
  MatProgressSpinnerModule,
  MatProgressBarModule,
  MatRippleModule,
  MatBottomSheetModule,
  MatDialogModule,
  MatSnackBarModule,
  MatTooltipModule,
  MatPaginatorModule,
  MatSortModule,
  MatTableModule
];

const NZ_MODULES = [
  NzSpaceModule,
  NzStepsModule,
  NzGridModule,
  NzRateModule,
  NzButtonModule,
  NzToolTipModule,
  NzIconModule,
  NzCollapseModule,
  NzAutocompleteModule,
  NzBadgeModule,
  NzCardModule,
  NzTagModule,
  NzCarouselModule,
  NzModalModule,
  NzDropDownModule,
  NzMenuModule,
  NzSelectModule,
  NzInputModule,
  NzSwitchModule,
  NzSpinModule,
  NzCheckboxModule,
  NzFormModule,
  NzTabsModule,
  NzRadioModule,
  NzDividerModule,
  NzInputNumberModule,
  NzProgressModule,
  NzTimePickerModule,
  NzPopconfirmModule,
  NzTableModule,
  NzPaginationModule,
  NzI18nModule,
  NzSkeletonModule,
  NzDatePickerModule,
  NzUploadModule,
  NzPopoverModule
];

const SHARED_COMPONENTS = [
  LoaderComponent,
  BaseComponent,
  UserSelectComponent,
  SectionDescriptionComponent,
  AlertComponent,
  SvgComponentComponent,
  ProfileWidgetComponent,
  GamificationWidgetComponent,
  GenericChartWidgetComponent,
  MonitoringWidgetComponent,
  HowIsGoingComponent,
  ReflectionWidgetComponent,
  GenericReflectionWidgetComponent,
  UsersPerformanceComponent,
  SbiWidgetComponent,
  GenericSbiCardComponent,
  Fb360WidgetComponent,
  ChartTransversalComponent,
  MonitoringIncrementalDeltaComponent,
  MonitoringIncrementalDeltaCardComponent,
  CardMonitoraggioComponent,
  MonitoringTableComponent,
  ModaleProfiloComponent,
  ImageFallbackDirective
];

@NgModule({
  declarations: [...SHARED_COMPONENTS],
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    FontAwesomeModule,
    NgChartsModule,
    NgxMatSelectSearchModule,
    ImageCropperModule,
    AngularEditorModule,
    NgxMatColorPickerModule,
    ScrollingModule,
    DragDropModule,
    ...MATERIAL_MODULES,
    ...NZ_MODULES
  ],
  exports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    FontAwesomeModule,
    NgChartsModule,
    NgxMatSelectSearchModule,
    ImageCropperModule,
    AngularEditorModule,
    NgxMatColorPickerModule,
    ScrollingModule,
    DragDropModule,
    ...MATERIAL_MODULES,
    ...NZ_MODULES,
    ...SHARED_COMPONENTS
  ]
})
export class SharedModule {}
